// DON'T ADD IMPORTS FROM CONTENTDEFINITION HERE. IT CAUSES BUILD ERRORS FOR SOME REASON.
import { createSelector } from 'reselect';

import { selectFlowsOnSiteEnabled } from '../../flow/selectors/FlowSelectors';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ContentFilterKeyName } from '../../ts/models/ContentFilters';
import { ExplorableItemType } from '../../ts/models/ContentType';
import { ExploreContentApi_ContentType } from '../../ts/ServerApi/types/IContent';
import { IFilterClause, IStringValuedFilterClause } from '../../ts/ServerApi/types/IFilterClause';
import { AppState } from '../state/AppState';
import { FilterMap } from '../state/ContentExplorerState';
import { convertSiteContentTypeToContentType, SiteSpaceContentType } from '../state/SiteSpaceContentTypeState';
import { isBackgroundJobsSpace, isFavoritesSpace, isRecentsSpace, isRecommendationsSpace, isSiteDataRoleSubspace, isSiteDatasourceSubspace, isSiteSpace, isSiteUserContentSubspace, isSomeKindaSearchSpace, IsWorkbookDatasourceSubspaceTab, SpaceInfo, isSharedWithMeSpace, isPersonalSpace } from '../state/SpaceInfo';
import { selectSpaceInfo } from './SpaceInfoSelectors';


// ---------- for project space

export const defaultContentTypeFilterValues: ReadonlyArray<ExploreContentApi_ContentType> = [
  'project',
  'workbook',
  'dataRole',
  'datasource',
];

export const defaultContentTypeFilterValuesWithFlows: ReadonlyArray<ExploreContentApi_ContentType> = [
  'project',
  'workbook',
  'datasource',
  'dataRole',
  'flow',
];

// ----- for favorites space (includes Views, which project space does not)

export const defaultContentTypeFilterValuesForFavoritesSpace: ReadonlyArray<ExploreContentApi_ContentType> = [
  'project',
  'workbook',
  'datasource',
  'view',
  'dataRole',
];

export const defaultContentTypeFilterValuesForSiteUserContentSubspace: ReadonlyArray<ExploreContentApi_ContentType> = [
  'project',
  'workbook',
  'datasource',
  'dataRole',
];

export const defaultContentTypeFilterValuesForDatasourcesSubspace: ReadonlyArray<ExploreContentApi_ContentType> = [
  'datasource'
];

export const defaultContentTypeFilterValuesForDataRolesSubspace: ReadonlyArray<ExploreContentApi_ContentType> = [
  'project',
];

export const defaultContentTypeFilterValuesForRecentsSpace: ReadonlyArray<ExploreContentApi_ContentType> = [
  'view'
];

export const defaultContentTypeFilterValuesWithMetricsForRecentsSpace: ReadonlyArray<ExploreContentApi_ContentType> = [
  'view',
  'metric'
];

export const defaultContentTypeFilterValuesWithMetricsAndPublishedConnectionsForRecentsSpace: ReadonlyArray<ExploreContentApi_ContentType> = [
  'view',
  'metric',
  'publishedConnection'
];


// Content type filter is unused for the search space
export const defaultEmptyContentTypeFilterValues: ReadonlyArray<ExploreContentApi_ContentType> = [];

export const defaultContentTypeFilterValuesForJobsSpace: ReadonlyArray<ExplorableItemType> = [
  'backgroundJob'
];

export const defaultContentTypeFilterValuesForMetricsSpace: ReadonlyArray<ExplorableItemType> = [
  'metric'
];

export const defaultContentTypeFilterValuesForPublishedConnectionsSpace: ReadonlyArray<ExplorableItemType> = [
  'publishedConnection'
];

export const defaultContentTypeFilterValuesForRecommendationsSpace: ReadonlyArray<ExplorableItemType> = [
  'recommendedView'
];

export const defaultContentTypeFilterValuesForSharedWithMeSpace: ReadonlyArray<ExploreContentApi_ContentType> = [
  'project',
  'workbook',
  'datasource',
  'view'
];

export const defaultContentTypeFilterValuesForPersonalSpace: ReadonlyArray<ExploreContentApi_ContentType> = [
  'workbook',
  'datasource',
  'dataRole',
  'flow',
];

export function selectFavoriteContentTypes(state: AppState): ReadonlyArray<ExploreContentApi_ContentType> {
  return selectFavoriteContentTypesSelector(state);
}

const selectFavoriteContentTypesSelector = createSelector(
  selectFlowsOnSiteEnabled,
  (isFlowsEnabled) => getFavoriteContentTypes(isFlowsEnabled)
);

export function selectSharedWithMeContentTypes(state: AppState): ReadonlyArray<ExploreContentApi_ContentType> {
  return selectSharedWithMeContentTypesSelector(state);
}

const selectSharedWithMeContentTypesSelector = createSelector(
  selectFlowsOnSiteEnabled,
  (isFlowsEnabled) => getSharedWithMeContentTypes(isFlowsEnabled)
);

function getSharedWithMeContentTypes(isFlowsEnabled: boolean): ReadonlyArray<ExploreContentApi_ContentType> {
  let contentTypes: Array<ExploreContentApi_ContentType> = Array.from(defaultContentTypeFilterValuesForSharedWithMeSpace);

  // TODO: While removing a feature flag ('contentType') from addConditionalContentTypesToBaseFilters
  // add 'contentType' to defaultContentTypeFilterValuesForSharedWithMeSpace
  contentTypes = addConditionalContentTypesToBaseFilters(contentTypes, isFlowsEnabled);

  if (FeatureFlags.collectionsEnabled &&
    FeatureFlags.sharedWithMeCollectionsEnabled) {
    contentTypes.push('collection');
  }
  return contentTypes;
}

export function getFavoriteContentTypes(isFlowsEnabled: boolean): ReadonlyArray<ExploreContentApi_ContentType> {
  let contentTypes = Array.from(defaultContentTypeFilterValuesForFavoritesSpace);

  // TODO: While removing a feature flag ('contentType') from addConditionalContentTypesToBaseFilters
  // add 'contentType' to defaultContentTypeFilterValuesForFavoritesSpace
  contentTypes = addConditionalContentTypesToBaseFilters(contentTypes as Array<ExploreContentApi_ContentType>, isFlowsEnabled);

  if (FeatureFlags.useCollectionsFavorites) {
    contentTypes.push('collection');
  }

  return contentTypes;
}

function getSiteUserContentTypes(isFlowsEnabled: boolean): ReadonlyArray<ExploreContentApi_ContentType> {
  let contentTypes: Array<ExploreContentApi_ContentType> = Array.from(defaultContentTypeFilterValuesForSiteUserContentSubspace);

  // While removing a feature flag ('contentType') from addConditionalContentTypesToBaseFilters
  // add 'contentType' to defaultContentTypeFilterValuesForSiteUserContentSubspace
  return addConditionalContentTypesToBaseFilters(contentTypes, isFlowsEnabled);
}

/**
 * A helper method to add `flow`, `metric`, `publishedConnection` content type
 * to filter, depending on whether they are available.
 * TODO: While removing the FeatureFlag, make sure you add to defaultContentTypeFilterValuesForXXXSpace
 * @param baseContentTypes Content filters which are always available for a space
 * @param isFlowsEnabled
 */
function addConditionalContentTypesToBaseFilters(
  baseContentTypes: Array<ExploreContentApi_ContentType>,
  isFlowsEnabled: boolean): Array<ExploreContentApi_ContentType> {

  if (isFlowsEnabled) {
    baseContentTypes.push('flow');
  }

  if (FeatureFlags.metricsSpaceEnabled) {
    baseContentTypes.push('metric');
  }

  if (FeatureFlags.publishedConnectionContentManagementEnabled) {
    baseContentTypes.push('publishedConnection');
  }

  return baseContentTypes;
}

// This memoizes the array, ensuring we don't create new references unnecessarily.
const selectDefaultContentTypeFilterValuesSelector = createSelector(
  [selectSpaceInfo, selectFlowsOnSiteEnabled],
  (spaceInfo, isFlowsEnabled) => getDefaultContentTypeFilterValues(spaceInfo, isFlowsEnabled)
);

export function getDefaultContentTypeFilterValues(spaceInfo: SpaceInfo, isFlowsEnabled: boolean): ReadonlyArray<ExplorableItemType> {
  if (isSiteDataRoleSubspace(spaceInfo)) {
    return defaultContentTypeFilterValuesForDataRolesSubspace;
  }

  if (IsWorkbookDatasourceSubspaceTab(spaceInfo) || isSiteDatasourceSubspace(spaceInfo)) {
    return defaultContentTypeFilterValuesForDatasourcesSubspace;
  }

  if (isFavoritesSpace(spaceInfo)) {
    return getFavoriteContentTypes(isFlowsEnabled);
  }

  if (isPersonalSpace(spaceInfo)) {
    return defaultContentTypeFilterValuesForPersonalSpace;
  }

  if (isSiteUserContentSubspace(spaceInfo)) {
    return getSiteUserContentTypes(isFlowsEnabled);
  }

  if (isRecentsSpace(spaceInfo)) {
    if (FeatureFlags.metricsSpaceEnabled) {
      return defaultContentTypeFilterValuesWithMetricsForRecentsSpace;
    } else if (FeatureFlags.publishedConnectionContentManagementEnabled) {
      return defaultContentTypeFilterValuesWithMetricsAndPublishedConnectionsForRecentsSpace;
    } else {
      return defaultContentTypeFilterValuesForRecentsSpace;
    }
  }

  if (isSomeKindaSearchSpace(spaceInfo)) {
    return defaultEmptyContentTypeFilterValues;
  }

  if (isBackgroundJobsSpace(spaceInfo)) {
    return defaultContentTypeFilterValuesForJobsSpace;
  }

  if (isRecommendationsSpace(spaceInfo)) {
    return defaultContentTypeFilterValuesForRecommendationsSpace;
  }

  if (isSharedWithMeSpace(spaceInfo)) {
    return getSharedWithMeContentTypes(isFlowsEnabled);
  }

  let contentTypes: Array<ExploreContentApi_ContentType> = [];
  if (isFlowsEnabled) {
    contentTypes = Array.from(defaultContentTypeFilterValuesWithFlows);
  } else {
    contentTypes = Array.from(defaultContentTypeFilterValues);
  }

  // Remove with removal of FeatureFlags.metricsSpaceEnabled
  // TODO: add 'metric' to defaultContentTypeFilterValues and defaultContentTypeFilterValuesWithFlows
  if (FeatureFlags.metricsSpaceEnabled) {
    contentTypes.push('metric');
  }

  if (FeatureFlags.publishedConnectionContentManagementEnabled) {
    contentTypes.push('publishedConnection');
  }

  return contentTypes;
}

export function selectDefaultContentTypeFilterValues(state: AppState) {
  return selectDefaultContentTypeFilterValuesSelector(state);
}

export function selectProjectSpaceContentTypes(state: AppState): ReadonlyArray<ExplorableItemType> {
  return state.contentExplorer.contentTypeFilterValues;
}

export function selectEffectiveProjectSpaceContentTypes(state: AppState): ReadonlyArray<ExplorableItemType> {
  return state.contentExplorer.contentTypeFilterValues || selectDefaultContentTypeFilterValuesSelector(state);
}

export function selectMixedContentFilterClauses(state: AppState): FilterMap {
  return state.contentExplorer.mixedContentFilterClauses;
}

export function selectSearchText(state: AppState): string {
  return (state.contentExplorer.mixedContentFilterClauses && state.contentExplorer.mixedContentFilterClauses.search &&
    (state.contentExplorer.mixedContentFilterClauses.search as IStringValuedFilterClause).value)
    || '';
}

export function selectSpecificMixedContentFilterClause(state: AppState, filterType: ContentFilterKeyName): IFilterClause {
  return state.contentExplorer.mixedContentFilterClauses[filterType];
}

export function selectSiteSpaceContentType(state: AppState): SiteSpaceContentType {
  return state.spaceInfo.subspaceName as SiteSpaceContentType;
}

export function selectMixedContentFilters(state: AppState): FilterMap {
  return state.contentExplorer.mixedContentFilterClauses;
}

// This memoizes the array, ensuring we don't create new references unnecessarily.
const selectSiteSpaceFilterValues = createSelector(
  selectSiteSpaceContentType,
  (contentType) => [convertSiteContentTypeToContentType(contentType)]
);

export function selectCurrentlyApplicableContentTypes(state: AppState): ReadonlyArray<ExplorableItemType> {
  // This could be cleaned up by adding the current space (site or project) into the spaceInfo
  const spaceInfo = selectSpaceInfo(state);
  return isSiteSpace(spaceInfo)
    ? selectSiteSpaceFilterValues(state)
    : selectEffectiveProjectSpaceContentTypes(state);
}

export function selectHiddenContentTypeForCurrentSpace(state: AppState) {
  return selectHiddenContentTypeForCurrentSpaceSelector(state);
}

// This memoizes the array, ensuring we don't create new references unnecessarily.
const selectHiddenContentTypeForCurrentSpaceSelector = createSelector(
  [selectSpaceInfo],
  (spaceInfo) => getHiddenContentType(spaceInfo)
);

function getHiddenContentType(spaceInfo: SpaceInfo): ReadonlyArray<ExplorableItemType> {
  // In case you want to hide a particular content type from content type filter dropdown for a space
  // add it here
  return isSharedWithMeSpace(spaceInfo) ? ['dataRole'] : [];
}
